<template>
  <v-card elevation="2">
    <v-card-title class="d-flex align-center">
      <v-icon left>mdi-format-list-bulleted</v-icon>
      <h3>Government Purchase Orders</h3>
      <v-spacer></v-spacer>
      <v-btn icon @click="refreshTable" :loading="loading" color="primary">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text>
      <!-- On-time delivery summary (based on current branch filter) -->
      <v-row class="mb-2">
        <v-col cols="12" sm="4">
          <v-card outlined class="pa-3 text-center summary-tile">
            <div class="caption grey--text text-uppercase">
              Delivered / Completed
            </div>
            <div class="text-h5 font-weight-bold">
              {{ deliverySummary.delivered }}
            </div>
            <div class="caption grey--text">
              of {{ deliverySummary.total }} PO(s)
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card outlined class="pa-3 text-center summary-tile">
            <div class="caption grey--text text-uppercase">
              Delivered On Time
            </div>
            <div class="text-h5 font-weight-bold green--text">
              {{ deliverySummary.onTime }}
            </div>
            <div class="caption grey--text">
              {{ deliverySummary.late }} late
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card outlined class="pa-3 text-center summary-tile">
            <div class="caption grey--text text-uppercase">
              On-Time Rate
            </div>
            <div
              class="text-h5 font-weight-bold"
              :class="onTimeRateColorClass"
            >
              {{ deliverySummary.onTimePct }}%
            </div>
            <v-progress-linear
              :value="deliverySummary.onTimePct"
              :color="onTimeRateColor"
              height="6"
              rounded
              class="mt-1"
            ></v-progress-linear>
          </v-card>
        </v-col>
      </v-row>

      <!-- Completion tabs: keeps outstanding (undelivered) POs visible/separated -->
      <v-tabs
        v-model="activeTab"
        class="mb-4"
        show-arrows
        background-color="transparent"
      >
        <v-tab href="#outstanding">
          <v-icon left small>mdi-truck-alert-outline</v-icon>
          Outstanding
          <v-chip
            x-small
            dark
            :color="tabCounts.outstanding ? 'orange darken-1' : 'grey'"
            class="ml-2 font-weight-bold"
          >
            {{ tabCounts.outstanding }}
          </v-chip>
        </v-tab>
        <v-tab href="#completed">
          <v-icon left small>mdi-check-circle-outline</v-icon>
          Completed
          <v-chip x-small dark color="green" class="ml-2 font-weight-bold">
            {{ tabCounts.completed }}
          </v-chip>
        </v-tab>
        <v-tab href="#cancelled">
          <v-icon left small>mdi-cancel</v-icon>
          Cancelled
          <v-chip
            x-small
            dark
            color="grey darken-1"
            class="ml-2 font-weight-bold"
          >
            {{ tabCounts.cancelled }}
          </v-chip>
        </v-tab>
        <v-tab href="#all">
          <v-icon left small>mdi-format-list-bulleted</v-icon>
          All
          <v-chip x-small dark color="blue" class="ml-2 font-weight-bold">
            {{ tabCounts.all }}
          </v-chip>
        </v-tab>
      </v-tabs>

      <v-alert
        v-if="activeTab === 'outstanding' && tabCounts.outstanding"
        dense
        text
        type="warning"
        border="left"
        class="mb-4"
      >
        <strong>{{ tabCounts.outstanding }}</strong>
        purchase order{{ tabCounts.outstanding > 1 ? "s are" : " is" }} not yet
        delivered / completed.
      </v-alert>

      <v-row>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="search"
            label="Search"
            prepend-icon="mdi-magnify"
            clearable
            outlined
            dense
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="filterBranch"
            :items="branchFilterOptions"
            label="Filter by Branch"
            prepend-icon="mdi-office-building"
            outlined
            dense
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="filterStatus"
            :items="['All', ...statusOptions]"
            label="Filter by Status"
            prepend-icon="mdi-filter"
            outlined
            dense
            clearable
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="filterApproval"
            :items="['All', 'Yes', 'No']"
            label="Filter by Approval"
            prepend-icon="mdi-check-circle"
            outlined
            dense
            clearable
          ></v-select>
        </v-col>
      </v-row>

      <v-data-table
        :headers="headers"
        :items="filteredPurchaseOrders"
        :search="search"
        :loading="loading"
        class="elevation-1"
        :items-per-page="10"
        :footer-props="{
          'items-per-page-options': [5, 10, 25, 50, -1],
        }"
      >
        <!-- PO Types -->
        <template v-slot:[`item.po_types`]="{ item }">
          <div
            v-if="item.po_types && item.po_types.length"
            class="d-flex flex-wrap"
            style="gap: 2px"
          >
            <template v-for="type in item.po_types">
              <v-chip
                v-if="type === 'customer_po'"
                :key="type"
                :color="getCustomerPoChipColor(item.customer_po_subtype)"
                dark
                class="mb-1"
              >
                Government PO{{
                  item.customer_po_subtype
                    ? " - " + getSubtypeLabel(item.customer_po_subtype)
                    : ""
                }}
              </v-chip>
              <v-chip
                v-else
                :key="type"
                :color="getPoTypeColor(type)"
                dark
                class="mb-1"
              >
                {{ getPoTypeLabel(type) }}
              </v-chip>
            </template>
          </div>
          <span v-else class="grey--text">--</span>
        </template>

        <!-- PO Number -->
        <template v-slot:[`item.po_number`]="{ item }">
          <v-tooltip bottom :disabled="item.po_number.length <= 20">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                color="blue"
                outlined
                v-bind="attrs"
                v-on="on"
                class="po-number-chip"
              >
                {{
                  item.po_number.length > 20
                    ? item.po_number.substring(0, 20) + "..."
                    : item.po_number
                }}
              </v-chip>
            </template>
            <span>{{ item.po_number }}</span>
          </v-tooltip>
        </template>

        <!-- Items Count -->
        <template v-slot:[`item.items_count`]="{ item }">
          <v-chip
            outlined
            color="primary"
            v-if="item.items && item.items.length"
          >
            {{ item.items.length }} item{{ item.items.length !== 1 ? "s" : "" }}
          </v-chip>
          <span v-else class="grey--text">0</span>
        </template>

        <!-- Amount -->
        <template v-slot:[`item.amount`]="{ item }">
          <span class="font-weight-bold" v-if="item.amount">
            ₱{{ formatAmount(item.amount) }}
          </span>
          <span v-else class="grey--text">--</span>
        </template>

        <!-- Status -->
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :color="getStatusColor(item.status)" dark>
            {{ item.status }}
          </v-chip>
          <v-chip
            v-if="getDeliveryStatus(item)"
            :color="
              getDeliveryStatus(item) === 'delivered' ? 'green' : 'orange'
            "
            dark
            small
            class="ml-1"
          >
            {{
              getDeliveryStatus(item) === "delivered" ? "Delivered" : "Partial"
            }}
          </v-chip>
        </template>

        <!-- Approved -->
        <template v-slot:[`item.approved`]="{ item }">
          <v-chip :color="item.approved === 'Yes' ? 'green' : 'grey'" dark>
            {{ item.approved || "No" }}
          </v-chip>
        </template>

        <!-- Attachments -->
        <template v-slot:[`item.attachment_path`]="{ item }">
          <div v-if="item.attachments && item.attachments.length > 0">
            <v-badge :content="item.attachments.length" color="primary" overlap>
              <v-btn
                icon
                color="primary"
                @click="viewItem(item)"
                title="View Attachments"
              >
                <v-icon>mdi-paperclip</v-icon>
              </v-btn>
            </v-badge>
          </div>
          <v-btn
            v-else-if="item.attachment_path"
            icon
            color="primary"
            @click="viewLegacyAttachment(item.attachment_path)"
            title="View Attachment"
          >
            <v-icon>mdi-paperclip</v-icon>
          </v-btn>
          <span v-else class="grey--text">--</span>
        </template>

        <!-- PO Date (Filed) -->
        <template v-slot:[`item.po_date`]="{ item }">
          {{ formatDate(item.po_date) }}
        </template>

        <!-- Completed Date -->
        <template v-slot:[`item.completed_date`]="{ item }">
          <div v-if="item.completed_date" class="d-flex align-center">
            {{ formatDate(item.completed_date) }}
            <v-chip
              v-if="isOnTime(item) !== null"
              :color="isOnTime(item) ? 'green' : 'red'"
              dark
              x-small
              class="ml-1"
            >
              {{ isOnTime(item) ? "On time" : "Late" }}
            </v-chip>
          </div>
          <span v-else class="grey--text">--</span>
        </template>

        <!-- Delivery Term / Due -->
        <template v-slot:[`item.delivery`]="{ item }">
          <div v-if="item.delivery_term_days">
            <div>{{ item.delivery_term_days }} working days</div>
            <div class="caption grey--text">
              Due {{ formatDate(deliveryDueDate(item)) }}
            </div>
          </div>
          <span v-else class="grey--text">--</span>
        </template>

        <!-- Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex">
            <v-btn icon color="primary" @click="viewItem(item)" title="View">
              <v-icon>mdi-eye</v-icon>
            </v-btn>
            <v-btn icon color="warning" @click="editItem(item)" title="Edit">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              color="error"
              @click="deleteItem(item)"
              title="Delete"
              :loading="item.deleting"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>

          </div>
        </template>

        <!-- No Data -->
        <template v-slot:no-data>
          <div class="text-center py-6">
            <v-icon size="64" color="grey lighten-1">mdi-cart-off</v-icon>
            <p class="grey--text mt-2">No purchase orders found</p>
          </div>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- View/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="900px" persistent scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">{{
            dialogMode === "view" ? "mdi-eye" : "mdi-pencil"
          }}</v-icon>
          {{ dialogMode === "view" ? "View" : "Edit" }} Purchase Order
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-form ref="editForm" v-model="editValid">
            <!-- Customer PO Sub-type -->
            <v-card outlined class="mb-4 pa-3">
              <v-card-subtitle class="pa-0 mb-2 font-weight-bold">
                Customer Type
              </v-card-subtitle>
              <v-row>
                <v-col cols="12">
                  <v-radio-group
                    v-model="editedItem.customer_po_subtype"
                    row
                    dense
                    hide-details
                    class="mt-0"
                    :disabled="dialogMode === 'view'"
                  >
                    <v-radio label="Walk-in" value="walk_in"></v-radio>
                    <v-radio label="Private" value="private"></v-radio>
                    <v-radio label="Government" value="government"></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>
            </v-card>

            <v-row>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="editedItem.customer_id"
                  :items="customers"
                  item-text="customer_name"
                  item-value="id"
                  label="Customer"
                  prepend-icon="mdi-account"
                  :disabled="dialogMode === 'view'"
                  outlined
                  dense
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.name"
                  label="Name"
                  prepend-icon="mdi-account-outline"
                  :disabled="dialogMode === 'view'"
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.department"
                  label="Department"
                  prepend-icon="mdi-domain"
                  :disabled="dialogMode === 'view'"
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.po_number"
                  label="PO Number"
                  prepend-icon="mdi-file-document"
                  :rules="[rules.required]"
                  :disabled="dialogMode === 'view'"
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.po_date"
                  label="PO Date (Filed)"
                  type="date"
                  prepend-icon="mdi-calendar"
                  :rules="[rules.required]"
                  :disabled="dialogMode === 'view'"
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model.number="editedItem.delivery_term_days"
                  :items="deliveryTermOptions"
                  label="Delivery Term"
                  prepend-icon="mdi-truck-fast"
                  :disabled="dialogMode === 'view'"
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.completed_date"
                  label="Completed Date"
                  type="date"
                  prepend-icon="mdi-calendar-check"
                  :disabled="dialogMode === 'view'"
                  outlined
                  dense
                  clearable
                  hint="Set when delivered / completed"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="editedItem.amount"
                  label="Amount"
                  type="number"
                  prepend-icon="mdi-currency-php"
                  :disabled="dialogMode === 'view'"
                  outlined
                  dense
                  min="0"
                  step="0.01"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.status"
                  :items="statusOptions"
                  label="Status"
                  prepend-icon="mdi-flag"
                  :rules="[rules.required]"
                  :disabled="dialogMode === 'view'"
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>

            <!-- Existing Attachments -->
            <div
              v-if="editedItem.attachments && editedItem.attachments.length > 0"
              class="mb-4"
            >
              <p class="subtitle-2 mb-2">
                <v-icon small class="mr-1">mdi-paperclip</v-icon>
                Attachments ({{ editedItem.attachments.length }})
              </p>
              <v-chip
                v-for="att in editedItem.attachments"
                :key="att.id"
                class="mr-2 mb-1"
                :close="dialogMode === 'edit'"
                @click:close="removeExistingAttachment(att)"
                @click="viewLegacyAttachment(att.file_path)"
                outlined
                color="primary"
              >
                <v-icon left small>mdi-file</v-icon>
                {{ att.original_name || "Attachment" }}
              </v-chip>
            </div>
            <!-- Legacy single attachment -->
            <div v-else-if="editedItem.attachment_path" class="mb-4">
              <v-chip
                color="primary"
                outlined
                @click="viewLegacyAttachment(editedItem.attachment_path)"
              >
                <v-icon left>mdi-file</v-icon>
                Legacy Attachment
              </v-chip>
            </div>

            <!-- Add new attachments -->
            <v-file-input
              v-if="dialogMode === 'edit'"
              v-model="newAttachmentFiles"
              label="Add Attachments (JPEG, PNG, PDF)"
              prepend-icon="mdi-paperclip"
              accept="image/*,.pdf"
              outlined
              dense
              clearable
              show-size
              multiple
              :hint="
                newAttachmentFiles && newAttachmentFiles.length > 0
                  ? newAttachmentFiles.length + ' new file(s) selected'
                  : ''
              "
              persistent-hint
            ></v-file-input>
            <div
              v-if="
                newAttachmentFiles &&
                newAttachmentFiles.length > 0 &&
                dialogMode === 'edit'
              "
              class="mb-4"
            >
              <v-chip
                v-for="(file, idx) in newAttachmentFiles"
                :key="'new-' + idx"
                close
                small
                class="mr-2 mb-1"
                color="green"
                outlined
                @click:close="newAttachmentFiles.splice(idx, 1)"
              >
                <v-icon left small>mdi-file-plus</v-icon>
                {{ file.name }}
              </v-chip>
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-4 d-block">
          <v-progress-linear
            v-if="submitting && uploadProgress > 0"
            :value="uploadProgress"
            color="primary"
            height="20"
            class="mb-3 rounded"
            striped
          >
            <template v-slot:default>
              <strong class="white--text caption">{{ uploadProgress }}%</strong>
            </template>
          </v-progress-linear>
          <div class="d-flex">
            <v-btn
              v-if="dialogMode === 'edit'"
              color="primary"
              :disabled="!editValid"
              :loading="submitting"
              @click="updateItem"
            >
              <v-icon left>mdi-content-save</v-icon>
              Update
            </v-btn>
            <v-btn text @click="closeDialog">
              <v-icon left>mdi-close</v-icon>
              Close
            </v-btn>
            <v-spacer></v-spacer>
            <p class="caption grey--text mb-0">ID: {{ editedItem.id }}</p>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Disapprove Dialog -->
    <v-dialog v-model="disapproveDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="orange white--text">
          <v-icon left color="white">mdi-close-circle</v-icon>
          Disapprove Purchase Order
        </v-card-title>
        <v-card-text class="pt-6">
          <p>
            Are you sure you want to disapprove PO #{{
              disapproveTarget ? disapproveTarget.po_number : ""
            }}?
          </p>
          <v-textarea
            v-model="disapproveRemarks"
            label="Remarks (reason for disapproval)"
            outlined
            dense
            rows="3"
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn text @click="disapproveDialog = false">Cancel</v-btn>
          <v-btn
            color="orange"
            dark
            @click="confirmDisapprove"
            :loading="disapproving"
          >
            Disapprove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Forward to Branch Modal -->
    <forward-to-branch-modal
      ref="forwardModal"
      @forward="handleForward"
      @error="handleForwardError"
    />
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ForwardToBranchModal from "../ForwardToBranchModal.vue";

export default {
  name: "CustomerPurchaseOrderTable",

  components: {
    ForwardToBranchModal,
  },

  data() {
    return {
      editValid: false,
      submitting: false,
      search: "",
      activeTab: "outstanding",
      filterBranch: null,
      filterStatus: "All",
      filterApproval: "All",
      dialog: false,
      dialogMode: "view",
      newAttachmentFiles: [],
      deletedAttachmentIds: [],
      uploadProgress: 0,

      // Disapprove dialog
      disapproveDialog: false,
      disapproveTarget: null,
      disapproveRemarks: "",
      disapproving: false,

      editedItem: {
        id: null,
        customer_id: null,
        po_number: "",
        po_date: "",
        completed_date: null,
        delivery_term_days: null,
        amount: 0,
        status: "",
        attachment_path: null,
        attachments: [],
        po_types: ["customer_po"],
        customer_po_subtype: null,
        name: "",
        department: "",
      },

      statusOptions: [
        "Pending",
        "Approved",
        "Processing",
        "Completed",
        "Cancelled",
        "On Hold",
      ],

      deliveryTermOptions: [
        { text: "15 working days", value: 15 },
        { text: "30 working days", value: 30 },
      ],

      headers: [
        { text: "Actions", value: "actions", sortable: false, width: 220 },
        { text: "ID", value: "id", width: 50 },
        { text: "PO #", value: "po_number", width: 120 },
        { text: "PO Types", value: "po_types", sortable: false, width: 180 },
        { text: "Items", value: "items_count", sortable: false, width: 90 },
        { text: "Customer", value: "customer.customer_name" },
        { text: "Name", value: "name" },
        { text: "PO Date (Filed)", value: "po_date", width: 130 },
        { text: "Completed Date", value: "completed_date", width: 150 },
        { text: "Delivery", value: "delivery", sortable: false, width: 150 },
        { text: "Amount", value: "amount", width: 120 },
        { text: "Status", value: "status", width: 110 },
        { text: "Approved", value: "approved", width: 100 },
        { text: "Files", value: "attachment_path", sortable: false, width: 70 },
      ],

      rules: {
        required: (value) => !!value || "This field is required",
        positive: (value) => value >= 0 || "Amount must be positive",
      },
    };
  },

  computed: {
    ...mapGetters({
      user: "auth/user",
      default_server_url: "auth/default_server_url",
      customer_purchase_orders:
        "customer_purchase_order/customer_purchase_orders",
      loading: "customer_purchase_order/loading",
      customers: "customer/customers",
      branches: "branch/branches",
    }),

    branchFilterOptions() {
      return [
        { text: "All Branches", value: "all" },
        ...this.branches.map((b) => ({ text: b.branch_name, value: b.id })),
      ];
    },

    // Branch-scoped POs, used as the base for both tab counts and the table.
    branchScopedPOs() {
      let filtered = this.customer_purchase_orders;
      if (this.filterBranch && this.filterBranch !== "all") {
        filtered = filtered.filter((po) => po.branch_id === this.filterBranch);
      }
      return filtered;
    },

    // Live counts per completion tab (drives the badges / notification).
    tabCounts() {
      const base = this.branchScopedPOs;
      return {
        outstanding: base.filter((po) => this.isOutstanding(po)).length,
        completed: base.filter((po) => po.status === "Completed").length,
        cancelled: base.filter((po) => po.status === "Cancelled").length,
        all: base.length,
      };
    },

    // On-time delivery scorecard across the current branch scope. A PO counts
    // as "delivered" once it has a completed_date; on-time when that date is
    // within its delivery_term_days (working days) from po_date.
    deliverySummary() {
      const base = this.branchScopedPOs;
      const delivered = base.filter((po) => !!po.completed_date);
      const onTime = delivered.filter((po) => this.isOnTime(po) === true).length;
      const late = delivered.length - onTime;
      const onTimePct = delivered.length
        ? Math.round((onTime / delivered.length) * 100)
        : 0;
      return {
        total: base.length,
        delivered: delivered.length,
        onTime,
        late,
        onTimePct,
      };
    },

    onTimeRateColor() {
      const pct = this.deliverySummary.onTimePct;
      if (pct >= 90) return "green";
      if (pct >= 70) return "orange";
      return "red";
    },

    onTimeRateColorClass() {
      return `${this.onTimeRateColor}--text`;
    },

    filteredPurchaseOrders() {
      let filtered = this.branchScopedPOs;

      // Filter by completion tab
      if (this.activeTab === "outstanding") {
        filtered = filtered.filter((po) => this.isOutstanding(po));
      } else if (this.activeTab === "completed") {
        filtered = filtered.filter((po) => po.status === "Completed");
      } else if (this.activeTab === "cancelled") {
        filtered = filtered.filter((po) => po.status === "Cancelled");
      }

      // Filter by status
      if (this.filterStatus && this.filterStatus !== "All") {
        filtered = filtered.filter((po) => po.status === this.filterStatus);
      }

      // Filter by approval
      if (this.filterApproval && this.filterApproval !== "All") {
        filtered = filtered.filter(
          (po) => (po.approved || "No") === this.filterApproval
        );
      }

      return filtered;
    },
  },

  methods: {
    ...mapActions({
      get_customer_purchase_orders:
        "customer_purchase_order/get_customer_purchase_orders",
      update_customer_purchase_order:
        "customer_purchase_order/update_customer_purchase_order",
      delete_customer_purchase_order:
        "customer_purchase_order/delete_customer_purchase_order",
      approve_customer_purchase_order:
        "customer_purchase_order/approve_customer_purchase_order",
      disapprove_customer_purchase_order:
        "customer_purchase_order/disapprove_customer_purchase_order",
      forward_customer_purchase_order:
        "customer_purchase_order/forward_customer_purchase_order",
      get_customers: "customer/get_customers",
      get_branches: "branch/get_branches",
      delete_attachment: "customer_purchase_order/delete_attachment",
    }),

    // Normalize historical rows onto the narrowed page: every record here is a
    // customer PO, so rows missing 'customer_po' in po_types are remapped.
    normalizePoTypes(poTypes) {
      return poTypes && poTypes.includes("customer_po")
        ? poTypes
        : ["customer_po"];
    },

    // Delivery progress across a PO's items: "delivered" when every item is
    // fully delivered, "partial" when at least one has some qty delivered.
    getDeliveryStatus(po) {
      if (!po.items || po.items.length === 0) return null;
      const fullyDelivered = po.items.filter(
        (i) => (i.qty_delivered || 0) >= i.qty
      ).length;
      if (fullyDelivered === po.items.length) return "delivered";
      const anyDelivered = po.items.some((i) => (i.qty_delivered || 0) > 0);
      return anyDelivered ? "partial" : null;
    },

    // A PO is "outstanding" (not yet delivered/completed) when it is neither
    // Completed nor Cancelled — i.e. Pending / Approved / Processing / On Hold.
    isOutstanding(po) {
      return po.status !== "Completed" && po.status !== "Cancelled";
    },

    // Parse a YYYY-MM-DD (or ISO) date string into a local Date at midnight,
    // avoiding the UTC shift that `new Date("YYYY-MM-DD")` introduces.
    parseLocalDate(dateStr) {
      if (!dateStr) return null;
      const d = String(dateStr).substring(0, 10).split("-");
      if (d.length !== 3) return new Date(dateStr);
      return new Date(+d[0], +d[1] - 1, +d[2]);
    },

    // Add N working days (skipping Sat/Sun) to a start date.
    addWorkingDays(startStr, days) {
      const start = this.parseLocalDate(startStr);
      if (!start || !days) return null;
      const date = new Date(start);
      let added = 0;
      while (added < days) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();
        if (day !== 0 && day !== 6) added++;
      }
      return date;
    },

    // The delivery deadline = po_date + delivery_term_days working days.
    deliveryDueDate(po) {
      if (!po.po_date || !po.delivery_term_days) return null;
      return this.addWorkingDays(po.po_date, po.delivery_term_days);
    },

    // true = delivered on/before the deadline, false = late,
    // null = not enough data to judge (no completed_date or no term).
    isOnTime(po) {
      if (!po.completed_date || !po.delivery_term_days) return null;
      const due = this.deliveryDueDate(po);
      const completed = this.parseLocalDate(po.completed_date);
      if (!due || !completed) return null;
      return completed.getTime() <= due.getTime();
    },

    async refreshTable() {
      await this.get_customer_purchase_orders();
      this.$emit("notification", "Table refreshed", "info");
    },

    viewItem(item) {
      this.editedItem = JSON.parse(
        JSON.stringify({
          ...item,
          po_types: this.normalizePoTypes(item.po_types),
          customer_po_subtype: item.customer_po_subtype || "walk_in",
          attachments: item.attachments || [],
        })
      );
      this.dialogMode = "view";
      this.dialog = true;
    },

    editItem(item) {
      this.editedItem = JSON.parse(
        JSON.stringify({
          ...item,
          po_types: this.normalizePoTypes(item.po_types),
          customer_po_subtype: item.customer_po_subtype || "walk_in",
          attachments: item.attachments || [],
        })
      );
      this.newAttachmentFiles = [];
      this.deletedAttachmentIds = [];
      this.dialogMode = "edit";
      this.dialog = true;
    },

    async removeExistingAttachment(att) {
      try {
        await this.delete_attachment({
          poId: this.editedItem.id,
          attachmentId: att.id,
        });
        this.editedItem.attachments = this.editedItem.attachments.filter(
          (a) => a.id !== att.id
        );
      } catch (error) {
        console.error("Error deleting attachment:", error);
      }
    },

    viewLegacyAttachment(filePath) {
      if (filePath) {
        const path = filePath.startsWith("uploads/")
          ? filePath
          : `storage/${filePath}`;
        const url = `${this.default_server_url}/${path}`;
        window.open(url, "_blank");
      }
    },

    async updateItem() {
      if (!this.$refs.editForm.validate()) {
        return;
      }

      this.submitting = true;
      this.uploadProgress = 0;

      try {
        const payload = {
          id: this.editedItem.id,
          customer_id: this.editedItem.customer_id,
          po_number: this.editedItem.po_number,
          po_date: this.editedItem.po_date,
          completed_date: this.editedItem.completed_date || "",
          delivery_term_days: this.editedItem.delivery_term_days || "",
          amount: this.editedItem.amount,
          status: this.editedItem.status,
          po_types: this.editedItem.po_types,
          customer_po_subtype: this.editedItem.customer_po_subtype,
          name: this.editedItem.name,
          department: this.editedItem.department,
        };

        if (this.newAttachmentFiles && this.newAttachmentFiles.length > 0) {
          // Use FormData for file upload
          const formData = new FormData();
          Object.keys(payload).forEach((key) => {
            if (key === "po_types") {
              formData.append(key, JSON.stringify(payload[key]));
            } else if (payload[key] !== null && payload[key] !== undefined) {
              formData.append(key, payload[key]);
            }
          });
          this.newAttachmentFiles.forEach((file) => {
            formData.append("attachments[]", file);
          });
          formData.append("_method", "PUT");
          await this.update_customer_purchase_order({
            _formData: formData,
            onUploadProgress: (e) => {
              if (e.total) {
                this.uploadProgress = Math.round((e.loaded * 100) / e.total);
              }
            },
          });
        } else {
          await this.update_customer_purchase_order(payload);
        }

        this.$emit(
          "notification",
          "Purchase order updated successfully!",
          "success"
        );
        this.closeDialog();
      } catch (error) {
        console.error("Error updating purchase order:", error);
        this.$emit(
          "notification",
          error.response?.data?.message || "Failed to update purchase order",
          "error"
        );
      } finally {
        this.submitting = false;
        this.uploadProgress = 0;
      }
    },

    async approveItem(item) {
      try {
        await this.approve_customer_purchase_order(item.id);
        this.$emit(
          "notification",
          `PO #${item.po_number} approved!`,
          "success"
        );
        await this.refreshTable();
      } catch (error) {
        this.$emit(
          "notification",
          error.response?.data?.message || "Failed to approve",
          "error"
        );
      }
    },

    openDisapproveDialog(item) {
      this.disapproveTarget = item;
      this.disapproveRemarks = "";
      this.disapproveDialog = true;
    },

    async confirmDisapprove() {
      if (!this.disapproveTarget) return;
      this.disapproving = true;
      try {
        await this.disapprove_customer_purchase_order({
          id: this.disapproveTarget.id,
          remarks: this.disapproveRemarks,
        });
        this.$emit(
          "notification",
          `PO #${this.disapproveTarget.po_number} disapproved`,
          "warning"
        );
        this.disapproveDialog = false;
        await this.refreshTable();
      } catch (error) {
        this.$emit(
          "notification",
          error.response?.data?.message || "Failed to disapprove",
          "error"
        );
      } finally {
        this.disapproving = false;
      }
    },

    viewAttachment(item) {
      // Legacy: open single attachment
      if (item.attachment_path) {
        this.viewLegacyAttachment(item.attachment_path);
      }
    },

    async deleteItem(item) {
      const confirmed = await this.$confirm({
        title: "Delete Purchase Order",
        message: `Are you sure you want to delete PO #${item.po_number}?`,
        color: "red",
        icon: "mdi-delete",
      });

      if (!confirmed) return;

      this.$set(item, "deleting", true);

      try {
        await this.delete_customer_purchase_order(item.id);

        this.$emit(
          "notification",
          "Purchase order deleted successfully!",
          "success"
        );
      } catch (error) {
        console.error("Error deleting purchase order:", error);
        this.$emit(
          "notification",
          error.response?.data?.message || "Failed to delete purchase order",
          "error"
        );
      } finally {
        this.$set(item, "deleting", false);
      }
    },

    closeDialog() {
      this.dialog = false;
      this.newAttachmentFiles = [];
      this.deletedAttachmentIds = [];
      setTimeout(() => {
        this.editedItem = {
          id: null,
          customer_id: null,
          po_number: "",
          po_date: "",
          completed_date: null,
          delivery_term_days: null,
          amount: 0,
          status: "",
          attachment_path: null,
          attachments: [],
          po_types: ["customer_po"],
          customer_po_subtype: null,
          name: "",
          department: "",
        };
        this.dialogMode = "view";
      }, 300);
    },

    formatAmount(amount) {
      return parseFloat(amount).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    getStatusColor(status) {
      const colors = {
        Pending: "orange",
        Approved: "blue",
        Processing: "purple",
        Completed: "green",
        Cancelled: "red",
        "On Hold": "grey",
      };
      return colors[status] || "grey";
    },

    getPoTypeColor(type) {
      const colors = {
        customer_po: "blue",
        items_for_stock: "teal",
        tools_and_equipment: "purple",
        others: "brown",
      };
      return colors[type] || "grey";
    },

    getPoTypeLabel(type) {
      const labels = {
        customer_po: "Government PO",
        items_for_stock: "Stock",
        tools_and_equipment: "Tools/Equip",
        others: "Others",
      };
      return labels[type] || type;
    },

    getCustomerPoChipColor(subtype) {
      const colors = {
        walk_in: "primary",
        private: "teal",
        government: "purple",
      };
      return colors[subtype] || "primary";
    },

    getSubtypeColor(subtype) {
      const colors = {
        walk_in: "orange",
        private: "cyan",
        government: "indigo",
      };
      return colors[subtype] || "grey";
    },

    getSubtypeLabel(subtype) {
      const labels = {
        walk_in: "Walk-in",
        private: "Private",
        government: "Government",
      };
      return labels[subtype] || subtype;
    },

    openForwardModal(item) {
      this.$refs.forwardModal.open(item, "Purchase Order");
    },

    async handleForward(payload) {
      try {
        const response = await this.forward_customer_purchase_order(payload);
        this.$emit(
          "notification",
          response.message ||
            "Purchase order and related records forwarded successfully!",
          "success"
        );
        this.$refs.forwardModal.close();
        await this.refreshTable();
      } catch (error) {
        console.error("Error forwarding purchase order:", error);
        this.$refs.forwardModal.resetForwarding();
        this.$emit(
          "notification",
          error.response?.data?.message ||
            "Failed to forward purchase order to another branch",
          "error"
        );
      }
    },

    handleForwardError(message) {
      this.$emit("notification", message, "error");
    },
  },

  async created() {
    this.filterBranch =
      this.user && this.user.branch_id ? this.user.branch_id : "all";
    await Promise.all([
      this.get_customer_purchase_orders(),
      this.customers.length === 0 ? this.get_customers() : null,
      this.branches.length === 0 ? this.get_branches() : null,
    ]);
  },
};
</script>

<style scoped>
.v-card {
  border-radius: 8px;
}
.po-number-chip {
  max-width: 200px;
}
</style>
